import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { clinicAPI } from "../../../services/api";
import Navbar from "../../../components/account/Navbar";
import TeamTabs from "./TeamTabs";
import TeamMemberGrid from "./TeamMemberGrid";
import InviteModal from "./InviteModal";
import ConfirmModal from "./ConfirmModal";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../utils/notificationUtils";
import "../../../styles/team.css";

const ManageTeam = ({ clinicData, clinicId }) => {
  const { t, i18n } = useTranslation();

  const [members, setMembers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("team");
  const [teamSearch, setTeamSearch] = useState("");
  const [inviteMode, setInviteMode] = useState(null); // "veterinarian" | "moderator"

  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    title: "",
    body: "",
    confirmText: "",
    onConfirm: null,
    isLoading: false,
  });

  const clinicCountryId = clinicData?.clinic?.country_id || clinicData?.clinic?.countryId || null;

  const fetchTeam = useCallback(async () => {
    if (!clinicId) {
      setError(t("manageTeam.errors.missingClinic"));
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await clinicAPI.getClinicTeam(clinicId);
      const data = response?.data?.data;
      if (response?.data?.status === "success" && data) {
        setMembers(data.members || []);
        setPendingInvitations(data.pending_invitations || []);
      } else {
        throw new Error(response?.data?.message || t("manageTeam.errors.fetchMembers"));
      }
    } catch (err) {
      const message = err?.response?.data?.message || err.message || t("manageTeam.errors.fetchMembers");
      setError(message);
      showErrorToast(message, { title: t("manageTeam.toasts.loadFailed.title"), duration: 5000 });
    } finally {
      setIsLoading(false);
    }
  }, [clinicId, t]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  const filteredMembers = useMemo(() => {
    if (!teamSearch.trim()) return members;
    const query = teamSearch.toLowerCase();
    return members.filter((member) => {
      const doctor = member.doctor || member.user || {};
      const name = `${doctor.first_name || ""} ${doctor.last_name || ""} ${doctor.name || ""}`.toLowerCase();
      const email = (doctor.email || "").toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }, [members, teamSearch]);

  const handleRemove = async (memberId) => {
    setConfirmConfig((prev) => ({ ...prev, isLoading: true }));
    try {
      const response = await clinicAPI.removeClinicMember(memberId);
      if (response?.data?.status === "success") {
        setMembers((prev) => prev.filter((m) => m.id !== memberId));
        showSuccessToast(t("manageTeam.toasts.removeSuccess.body"), {
          title: t("manageTeam.toasts.removeSuccess.title"),
          duration: 3000,
        });
      } else {
        throw new Error(response?.data?.message || t("manageTeam.errors.removeFailed"));
      }
    } catch (err) {
      const message = err?.response?.data?.message || err.message || t("manageTeam.errors.removeFailed");
      showErrorToast(message, { title: t("manageTeam.toasts.removeFailed.title"), duration: 5000 });
    } finally {
      closeConfirm();
    }
  };

  const handleCancel = async (invitationId) => {
    setConfirmConfig((prev) => ({ ...prev, isLoading: true }));
    try {
      const response = await clinicAPI.removeClinicMember(invitationId);
      if (response?.data?.status === "success") {
        setPendingInvitations((prev) => prev.filter((m) => m.id !== invitationId));
        showSuccessToast(t("manageTeam.toasts.cancelSuccess.body"), {
          title: t("manageTeam.toasts.cancelSuccess.title"),
          duration: 3000,
        });
      } else {
        throw new Error(response?.data?.message || t("manageTeam.errors.cancelInvite"));
      }
    } catch (err) {
      const message = err?.response?.data?.message || err.message || t("manageTeam.errors.cancelInvite");
      showErrorToast(message, { title: t("manageTeam.toasts.cancelFailed.title"), duration: 5000 });
    } finally {
      closeConfirm();
    }
  };

  const openRemoveConfirm = (memberId) => {
    setConfirmConfig({
      isOpen: true,
      title: t("manageTeam.removeModal.title"),
      body: t("manageTeam.removeModal.body"),
      confirmText: t("manageTeam.removeModal.confirm"),
      onConfirm: () => handleRemove(memberId),
      isLoading: false,
      confirmVariant: "danger",
    });
  };

  const openCancelConfirm = (invitationId) => {
    setConfirmConfig({
      isOpen: true,
      title: t("manageTeam.cancelModal.title"),
      body: t("manageTeam.cancelModal.body"),
      confirmText: t("manageTeam.cancelModal.confirm"),
      onConfirm: () => handleCancel(invitationId),
      isLoading: false,
      confirmVariant: "danger",
    });
  };

  const closeConfirm = () => {
    setConfirmConfig((prev) => ({ ...prev, isOpen: false, isLoading: false }));
  };

  const handleInviteSuccess = () => {
    fetchTeam();
  };

  const renderError = () => (
    <div className="team-empty" dir={i18n.dir()} lang={i18n.language}>
      <div className="team-empty-icon" style={{ background: "#fee2e2", color: "#dc2626" }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h3 className="team-empty-title">{t("manageTeam.toasts.loadFailed.title", "Failed to load team")}</h3>
      <p className="team-empty-text">{error}</p>
      <button type="button" className="team-btn team-btn-primary" onClick={fetchTeam}>
        {t("clinicInfo.retry", "Retry")}
      </button>
    </div>
  );

  return (
    <div className="clinicsetup-page-wrapper clinicinfo-member-wrapper" dir={i18n.dir()} lang={i18n.language}>
      <Navbar />

      <div className="team-page">
        <div className="team-header">
          <div className="team-header-content">
            <h2 className="team-title">{t("manageTeam.title", "Manage Team")}</h2>
            <p className="team-description">
              {t("manageTeam.description", "Invite veterinarians and moderators to work at your clinic.")}
            </p>
          </div>
          <div className="team-actions">
            <button
              type="button"
              className="team-btn team-btn-primary"
              onClick={() => setInviteMode("veterinarian")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 5v14M5 12h14" />
              </svg>
              {t("manageTeam.buttons.inviteVeterinarian", "Invite Veterinarian")}
            </button>
            <button
              type="button"
              className="team-btn team-btn-secondary"
              onClick={() => setInviteMode("moderator")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 5v14M5 12h14" />
              </svg>
              {t("manageTeam.buttons.inviteModerator", "Invite Clinic Moderator")}
            </button>
          </div>
        </div>

        {error ? (
          renderError()
        ) : (
          <>
            <TeamTabs
              activeTab={activeTab}
              onChange={setActiveTab}
              teamCount={members.length}
              pendingCount={pendingInvitations.length}
            />

            {activeTab === "team" ? (
              <TeamMemberGrid
                members={filteredMembers}
                searchQuery={teamSearch}
                onSearchChange={setTeamSearch}
                onRemove={openRemoveConfirm}
                onCancel={openCancelConfirm}
                isLoading={isLoading}
                emptyTitle={t("manageTeam.current.empty", "No team members yet")}
                emptyText={t("manageTeam.empty.inviteTeam", "Invite veterinarians or moderators to start building your team.")}
                emptyAction={
                  <button
                    type="button"
                    className="team-btn team-btn-primary"
                    onClick={() => setInviteMode("veterinarian")}
                  >
                    {t("manageTeam.buttons.inviteVeterinarian", "Invite Veterinarian")}
                  </button>
                }
              />
            ) : (
              <TeamMemberGrid
                members={pendingInvitations}
                searchQuery=""
                onSearchChange={() => {}}
                onRemove={() => {}}
                onCancel={openCancelConfirm}
                isLoading={isLoading}
                emptyTitle={t("manageTeam.pending.empty", "No pending invitations")}
                emptyText={t("manageTeam.pending.emptyText", "All invitations have been responded to.")}
                showSearch={false}
              />
            )}
          </>
        )}
      </div>

      <InviteModal
        isOpen={!!inviteMode}
        onClose={() => setInviteMode(null)}
        mode={inviteMode}
        clinicId={clinicId}
        clinicCountryId={clinicCountryId}
        onSuccess={handleInviteSuccess}
      />

      <ConfirmModal
        isOpen={confirmConfig.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        body={confirmConfig.body}
        confirmText={confirmConfig.confirmText}
        cancelText={activeTab === "pending" ? t("manageTeam.cancelModal.keep") : t("manageTeam.removeModal.keep")}
        isLoading={confirmConfig.isLoading}
        confirmVariant={confirmConfig.confirmVariant}
      />
    </div>
  );
};

export default ManageTeam;
